import { useState } from "react";
import { Alert, Box, Chip, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { nearExpiryApi, type NearExpiryRow } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { formatDate } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

/**
 * UC-M1-INV-09 — Near-expiry queue. Tabs select the days-window the spec calls out
 * (30 / 60 / 90). Already-expired batches surface at the top of the 30-day tab in red.
 */
export function NearExpiryPage() {
  const [windowDays, setWindowDays] = useState<30 | 60 | 90>(30);

  const query = useQuery({
    queryKey: ["near-expiry", windowDays],
    queryFn: () => nearExpiryApi.list(windowDays),
  });

  const columns: GridColDef<NearExpiryRow>[] = [
    { field: "expiryDate", headerName: "Expiry", width: 110,
      valueFormatter: (v) => v ? formatDate(v as string) : "" },
    { field: "daysToExpiry", headerName: "Days", width: 90, type: "number",
      renderCell: (p) => {
        const d = Number(p.value);
        const color: "default" | "error" | "warning" | "info" =
          d < 0 ? "error" : d <= 14 ? "error" : d <= 30 ? "warning" : "info";
        return <Chip size="small" color={color} label={d < 0 ? `expired ${-d}d` : `${d}d`} />;
      } },
    { field: "itemName", headerName: "Item", flex: 1.2, minWidth: 200,
      renderCell: (p) => (
        <Stack>
          <Typography variant="body2" fontWeight={500}>{p.row.itemName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.itemSku} · {[p.row.form, p.row.strength].filter(Boolean).join(" · ")}
          </Typography>
        </Stack>
      ) },
    { field: "batchNo", headerName: "Batch", width: 140 },
    { field: "warehouseName", headerName: "Warehouse", width: 160 },
    { field: "onHandQty", headerName: "On hand", width: 110, type: "number" },
    { field: "estimatedExposure", headerName: "Exposure", width: 130, type: "number",
      valueFormatter: (v) => inr.format(Number(v ?? 0)) },
  ];

  const rows = query.data ?? [];

  return (
    <Box>
      <PageHeader breadcrumbs={["Inventory"]} title="Near-expiry queue" />
      <Paper sx={{ p: 2 }}>
        <Tabs value={windowDays} onChange={(_, v) => setWindowDays(v)} sx={{ mb: 2 }}>
          <Tab value={30} label="≤ 30 days" />
          <Tab value={60} label="≤ 60 days" />
          <Tab value={90} label="≤ 90 days" />
        </Tabs>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={rows} columns={columns} loading={query.isLoading}
            getRowId={(row) => `${row.batchId}|${row.warehouseId}`}
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            pageSizeOptions={[25, 50, 100]}
            getRowHeight={() => 64}
          />
        </Box>
      </Paper>
    </Box>
  );
}
